// Varsel øverst i Pay-admin når Vipps melder problemer (status.vippsmobilepay.com, backend vipps/driftsstatus.ts).
// Viser bare noe når det er noe å si: brudd, redusert drift, vedlikehold nå eller snart, eller at Pay ikke har fått
// hentet statusen på en stund. Hentes hvert 2. minutt, som backenden.
import { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, Info, Wrench } from 'lucide-react';
import type { VippsDriftNivaa, VippsDriftsstatus } from '@pay/shared';
import { api } from '../api/client';

const STIL: Record<VippsDriftNivaa, string> = {
  feil: 'bg-red-50 border-red-300 text-red-900',
  forsinket: 'bg-amber-50 border-amber-300 text-amber-900',
  vedlikehold: 'bg-sky-50 border-sky-300 text-sky-900',
  ok: 'bg-sky-50 border-sky-300 text-sky-900', // planlagt vedlikehold
  ukjent: 'bg-slate-100 border-slate-300 text-slate-800',
};

const HENDELSE_STATUS: Record<string, string> = {
  investigating: 'undersøkes',
  identified: 'årsak funnet',
  monitoring: 'følges med',
};

const VEDLIKEHOLD_STATUS: Record<string, string> = {
  scheduled: 'planlagt',
  in_progress: 'pågår',
  verifying: 'kontrolleres',
};

const to = (n: number) => String(n).padStart(2, '0');
const klokke = (d: Date) => `${to(d.getHours())}:${to(d.getMinutes())}`;

/** «04.11. kl. 12:27» (lokal tid). */
function tid(iso: string): string {
  const d = new Date(iso);
  return `${to(d.getDate())}.${to(d.getMonth() + 1)}. kl. ${klokke(d)}`;
}

/** «05.11. kl. 02:39–03:39», eller med begge datoene når vedlikeholdet går over midnatt. */
function periode(start: string, slutt: string | null): string {
  if (!slutt) return tid(start);
  const a = new Date(start);
  const b = new Date(slutt);
  return a.toDateString() === b.toDateString() ? `${tid(start)}–${klokke(b)}` : `${tid(start)}–${tid(slutt)}`;
}

export function VippsDriftBanner() {
  const [s, setS] = useState<VippsDriftsstatus | null>(null);

  useEffect(() => {
    let avbrutt = false;
    const hent = () => {
      api
        .get<VippsDriftsstatus>('/api/admin/vipps-status')
        .then((x) => {
          if (!avbrutt) setS(x);
        })
        .catch(() => {
          /* neste runde prøver igjen */
        });
    };
    hent();
    const t = setInterval(hent, 120_000);
    return () => {
      avbrutt = true;
      clearInterval(t);
    };
  }, []);

  if (!s || !s.tekst) return null;
  const Ikon = s.nivaa === 'feil' || s.nivaa === 'forsinket' ? AlertTriangle : s.nivaa === 'ukjent' ? Info : Wrench;

  return (
    <div role="status" className={`border-b px-6 py-2.5 text-sm ${STIL[s.nivaa]}`}>
      <div className="flex items-start gap-3">
        <Ikon size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="font-medium">{s.tekst}</div>
          {(s.hendelser.length > 0 || s.vedlikehold.length > 0) && (
            <ul className="mt-1 space-y-0.5">
              {s.hendelser.map((h) => (
                <li key={h.lenke + h.navn}>
                  <a href={h.lenke} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                    {h.navn}
                  </a>{' '}
                  — {HENDELSE_STATUS[h.status] ?? h.status}, oppdatert {tid(h.oppdatert)}
                  {h.komponenter.length > 0 ? ` (${h.komponenter.join(', ')})` : ''}
                </li>
              ))}
              {s.vedlikehold.map((v) => (
                <li key={v.lenke + v.navn}>
                  <a href={v.lenke} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                    {v.navn}
                  </a>{' '}
                  — {VEDLIKEHOLD_STATUS[v.status] ?? v.status}, {periode(v.start, v.slutt)} ({v.komponenter.join(', ')})
                </li>
              ))}
            </ul>
          )}
        </div>
        <a
          href={s.lenke}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center gap-1 underline hover:no-underline"
        >
          Vipps' driftsstatus <ExternalLink size={12} aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}
